import type { Candidate } from './Candidate'
import type { DatabaseMetadata } from './DatabaseMetadata'
import type { NumberOrNull } from './NumberOrNull'
import type { Question, StatementKind, Test, TestGradingCategory } from './Test'
import type { UploadedFile } from './UploadedFile'
import type { TestSubmissionFlags } from './Submission'

export enum AttemptStatus {
  NotStarted = 'not-started',
  InProgress = 'in-progress',
  Incomplete = 'incomplete',
  Processing = 'processing',
  Error = 'error',
  Complete = 'complete',
}

export enum GradingStatus {
  NotStarted = 'not-started',
  InProgress = 'in-progress',
  Complete = 'complete',
  Queued = 'queued',
  Error = 'error',
  Timeout = 'timeout',
  Processing = 'processing',
  OutOfDate = 'out-of-date',
  GradeOverrideRequiresRegrade = 'grade-override-requires-regrade',
}

export enum CategoryScoreAllocation {
  ActualUserScore = 'actual-user-score',
  CategoryLevelScore = 'category-level-score',
}

export enum CategoryScoreWeighting {
  Manual = 'manual',
  Points = 'points',
}

export interface QuestionAttemptGradingCategoryGrade {
  // maps to written/sim grading category
  id: string
  questionAttemptId: string
  questionAttemptGradeId: string

  testGradingCategoryId: string
  level: number
  levelDescription: string
  score: number
  rationale: string

  overrideRationale: string | null
  overrideLevel: number | null
  overrideLevelDescription: string | null
  overrideScore: number | null
  isOverridden: boolean

  feedback: string
  maxPoints: number
}

export interface QuestionAttemptGrade {
  id: string
  questionAttemptId: string
  totalScore: number // question + confidence
  maxPoints: number
  questionScore: number // if written: sum of category scores

  // MCQ && Written
  confidenceScore: number | null

  // Written & simulation
  questionAttemptGradingCategoryGrades: QuestionAttemptGradingCategoryGrade[]
  remarks: string

  // MCQ && Likert
  testGradingCategoryId: string | null

  status: GradingStatus
}

export interface QuestionAttempt {
  id: string
  questionId: string // Question.id
  testId: string // Test.id
  candidateId: string

  // MCQ & Written
  confidence: number | null // 1-5

  // MCQ & Likert
  selectedChoiceId: string | null // Choice.id

  // Written
  answerText: string
  file: UploadedFile | null

  // Simulation
  interactionId: string | null

  status: AttemptStatus

  questionAttemptGrade: QuestionAttemptGrade | null
}

export interface GradedSecondaryGradeData {
  level: number
  levelTitle: string
  levelDescription: string
  rationale: string
  feedback: string
  isVariant: boolean
  scoreDisplay: number
  candidateScore: number
  gradingConfidenceRating: string
  gradingFailed?: false
}

// The second run couldn't grade the category.
export interface FailedSecondaryGradeData {
  level: null
  levelTitle: null
  levelDescription: null
  rationale: null
  feedback: null
  isVariant: boolean
  scoreDisplay: null
  candidateScore: null
  gradingFailed: true
}

export type SecondaryGradeData =
  | GradedSecondaryGradeData
  | FailedSecondaryGradeData

export interface TestAttemptGradingCategoryStatement {
  id: string
  statement: string | null
  // Reads 0 on a qualifying statement, which carries no points: ignore it there.
  points: number
  // On a qualifying statement, awarded means its condition was met.
  awarded: boolean
  rationale: string | null
  isOverridden: boolean
  aiAwarded?: boolean
  // Absent from API versions before qualifiers; treat absent as 'points'.
  kind?: StatementKind
}

export interface TestAttemptGradingCategoryGrade {
  id: string
  testGradingCategoryId: string
  level: number
  levelTitle: string
  levelDescription: string

  overrideLevel: number | null
  overrideRationale: string | null
  overrideLevelTitle: string | null
  overrideLevelDescription: string | null
  overrideScore: number | null
  isOverridden: boolean
  isEdited?: boolean

  categoryLevelScore: number
  candidateScore: number
  scoreDisplay: number
  maxPoints: number

  weighting: NumberOrNull

  rationale: string
  feedback: string

  gradingConfidenceRating: number
  hasVariableGrading?: boolean
  secondaryGradeData?: SecondaryGradeData | null
  // Points-based rubrics only; empty/undefined for level-based categories.
  statements?: TestAttemptGradingCategoryStatement[]
  // The graded category (incl. sub-question leaves), loaded on the detail
  // endpoint via the testGradingCategory relation.
  category?: TestGradingCategory
  displayIndex?: number
}

export interface TestAttemptGrade {
  id: string
  testAttemptId: string
  testId: string
  status: GradingStatus

  candidateScoresTotalUnweighted: number
  candidateScoresTotalWeighted: number
  categoryScoresTotalUnweighted: number
  categoryScoresTotalWeighted: number
  maxPointsUnweighted: number
  maxPointsWeighted: number

  scoreTotalDisplay: number
  maxPointsDisplay: number

  categoryScoreAllocation: CategoryScoreAllocation
  categoryScoreWeighting: CategoryScoreWeighting

  testAttemptGradingCategoryGrades: TestAttemptGradingCategoryGrade[]

  remarks: string

  updatedAt?: string
  gradedAt?: string | null
  summaryFeedback: string | null
  isEdited?: boolean
  summaryFeedbackEdited?: boolean
  summaryOutOfDateCategoryGradeIds?: string[]
}

export interface TestAttemptGradeCategoryEdit {
  categoryGradeId: string
  level?: number
  statements?: { statementGradeId: string; awarded: boolean }[]
  rationale?: string
  feedback?: string | null
}

export interface TestAttemptCategoryGradeDraft {
  level?: number
  levelTitle?: string
  statements?: Record<string, boolean>
  score?: number
  rationale?: string
  feedback?: string | null
}

export interface TestAttemptGradeEdits {
  updatedAt: string
  categories?: TestAttemptGradeCategoryEdit[]
  summaryFeedback?: string | null
}

export interface GenerateCategoryFeedbackRequest {
  categoryGradeId: string
  level?: number
  statements?: { statementGradeId: string; awarded: boolean }[]
  rationale: string
}

export interface GenerateSummaryFeedbackRequest {
  categories?: TestAttemptGradeCategoryEdit[]
}

/**
 * Why feedback generation failed, from the response body's `error` field
 * (studio-api decision 0020). Mirrors studio-api's `GradeFeedbackErrorCode`.
 */
export enum GradeFeedbackErrorCode {
  ModelNotConfigured = 'model_not_configured',
  ModelTimeout = 'model_timeout',
  ModelFailed = 'model_failed',
  SubmissionUnavailable = 'submission_unavailable',
}

export interface TestAttempt extends DatabaseMetadata {
  id: string
  testId: string
  candidateId: string
  status: AttemptStatus
  lastCompletedQuestionId: string | null // for resumability
  totalBlurTimeMs: number
  startedAt: string | null
  completedAt: string | null
  questionAttemptIds?: string[]
  questionAttempts?: QuestionAttempt[]
  testAttemptGrade: TestAttemptGrade | null

  autoGrade: boolean

  // Info-only submission flags written by the grader on grade ingest (TRV-1231/TRV-1275).
  flags?: TestSubmissionFlags

  interactionId: string | null
  ltieepLinkId: string | null
}

export interface TestAttemptFileSubmission {
  id: string
  testId: string
  testAttemptId: string
  path: string
  fileNameWithExtension: string
  mimeType: string
  createdAt: string
  updatedAt: string

  // TODO
  pathOriginal: string
  pathMarkdown: string
}

export interface QuestionAttemptWithQuestion extends QuestionAttempt {
  question: Question
}

export interface TestAttemptWithGradeAndCandidate extends TestAttempt {
  testAttemptGrade: TestAttemptGrade | null
  candidate: Candidate | null
}

export interface PopulatedTestAttempt extends TestAttemptWithGradeAndCandidate {
  questionAttempts: QuestionAttempt[]
  testAttemptFileSubmission?: TestAttemptFileSubmission
}

export interface TestGradingCategoryWithGrade extends TestGradingCategory {
  testAttemptGradingCategoryGrade?: TestAttemptGradingCategoryGrade
}

export interface QuestionWithAttempt extends Question {
  questionAttempt?: QuestionAttempt
}

export interface TestWithTestAttempt extends Test {
  testAttempt: TestAttempt | null
}
